import { formatInTimeZone } from "date-fns-tz";
import {
  formatQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";

export const DEFAULT_QUARTER_TIME = "09:00";

const FORM_TIMEZONE = "Asia/Bangkok";

const NAIVE_TIMESTAMP_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?$/;

function isOffsetOrUtcTimestamp(raw: string): boolean {
  return /Z$/i.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw);
}

/** Naive PG `timestamp` / form value → `"yyyy-MM-ddTHH:mm"` (quarter-snapped). */
export function toDateTimeLocalValue(
  value: Date | string | null | undefined,
): string {
  if (value == null || value === "") return "";

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return "";
    const wall = raw.match(NAIVE_TIMESTAMP_RE);
    if (wall && !isOffsetOrUtcTimestamp(raw)) {
      const date = wall[1];
      const { h, m } = parseQuarterClock(`${wall[2]}:${wall[3]}`);
      return `${date}T${formatQuarterHhMm(h, m)}`;
    }
  }

  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const date = formatInTimeZone(dt, FORM_TIMEZONE, "yyyy-MM-dd");
  const { h, m } = parseQuarterClock(
    formatInTimeZone(dt, FORM_TIMEZONE, "HH:mm"),
  );
  return `${date}T${formatQuarterHhMm(h, m)}`;
}

/**
 * Form `"yyyy-MM-ddTHH:mm"` → naive storage `"yyyy-MM-dd HH:mm:ss"`
 * (Asia/Bangkok wall clock — ตรง drizzle `timestamp` without time zone)
 */
export function dateTimeLocalValueToStorageTimestamp(
  local: string,
): string | null {
  const trimmed = local.trim();
  if (!trimmed) return null;
  const { date, time } = splitDateTimeLocalValue(trimmed);
  if (!date) return null;
  const { h, m } = parseQuarterClock(
    (time || DEFAULT_QUARTER_TIME).slice(0, 5),
  );
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date} ${pad(h)}:${pad(m)}:00`;
}

export function splitDateTimeLocalValue(value: unknown): {
  date: string;
  time: string;
} {
  if (value == null || value === "") return { date: "", time: "" };
  const raw = String(value).trim();
  if (!raw) return { date: "", time: "" };

  if (raw.includes("T")) {
    const [datePart, timePart = ""] = raw.split("T");
    const date = (datePart ?? "").slice(0, 10);
    const hhmm = timePart.slice(0, 5);
    if (!date) return { date: "", time: "" };
    if (!hhmm) return { date, time: "" };
    const { h, m } = parseQuarterClock(hhmm);
    return { date, time: formatQuarterHhMm(h, m) };
  }

  // Date-only
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return { date: raw, time: "" };
  }

  const wall = raw.match(NAIVE_TIMESTAMP_RE);
  if (wall && !isOffsetOrUtcTimestamp(raw)) {
    const date = wall[1];
    const { h, m } = parseQuarterClock(`${wall[2]}:${wall[3]}`);
    return { date, time: formatQuarterHhMm(h, m) };
  }

  // Fallback: parse as Date
  return splitDateTimeLocalValue(toDateTimeLocalValue(raw));
}

export function combineDateAndQuarterTime(
  date: string,
  time: string,
): string {
  const d = date.trim().slice(0, 10);
  if (!d) return "";
  const t = (time.trim() || DEFAULT_QUARTER_TIME).slice(0, 5);
  const { h, m } = parseQuarterClock(t);
  return `${d}T${formatQuarterHhMm(h, m)}`;
}

/** Parse `"yyyy-MM-dd"` as local Date at midnight. */
export function parseLocalDateOnly(date: string): Date | null {
  const d = date.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, mo, day] = d.split("-").map(Number);
  if (!y || !mo || !day) return null;
  const dt = new Date(y, mo - 1, day);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function formatLocalDateOnly(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
